<template>
    <div
        v-if="!isLoading"
        class="d-inline-flex w-100 tw-gap-0"
        :class="{
            'selector-actions': !!currentSupplier,
        }"
    >
        <Selector
            v-if="editable"
            ref="selector"
            v-model="model"
            :options="filteredSuppliers"
            :all-options="availableSuppliers"
            :max-displayed-options="5"
            placeholder="Type to search"
            :editable
            :input-class="currentSupplier && dropdownVisible ? '!tw-rounded-r-none !tw-border-r-0' : undefined"
            class="flex-fill"
            :class="{
                'has-dropdown': dropdownVisible,
            }"
        >
            <template v-if="currentSupplier && currentSupplier.is_archived" #prefix>
                <Tooltip :label="currentSupplier.name + ' has been archived'">
                    <i class="mdi mdi-archive mr-2"></i>
                </Tooltip>
            </template>
            <template v-else-if="currentSupplier && currentSupplier.has_supplier_account" #prefix>
                <Tooltip :label="currentSupplier.name + ' has an active contact user'">
                    <i class="mdi mdi-account-circle mr-2 text-primary"></i>
                </Tooltip>
            </template>
            <template v-else-if="currentSupplier && currentSupplier.xero_contact_id" #prefix>
                <Tooltip label="Linked with Xero">
                    <img src="@/images/brands/xero_blue.png" class="tw-w-5 tw-h-5 tw-mr-2" alt="Linked with Xero" />
                </Tooltip>
            </template>
            <template #optionLabel="{ option }">
                <div class="tw-flex tw-items-center tw-gap-2">
                    <img
                        v-if="(option as Supplier).xero_contact_id"
                        src="@/images/brands/xero_blue.png"
                        class="tw-w-5 tw-h-5"
                        alt="Linked with Xero"
                    />
                    {{ option.name }}
                </div>
            </template>
            <template #customOptions="{ searchValue, hasExactMatch }">
                <a
                    v-if="Auth.can('manage suppliers') && !hasExactMatch"
                    href="#"
                    @mousedown="createSupplier(searchValue)"
                >
                    <i class="mdi mdi-plus" />
                    Create {{ searchValue ? searchValue : ' a new ' + objectLabel }}
                </a>
            </template>
        </Selector>
        <span v-else>{{ currentSupplier ? currentSupplier.name : 'None' }}</span>
        <Menu v-if="editable && currentSupplier && dropdownVisible">
            <template #trigger>
                <div
                    class="tw-flex tw-items-center tw-border tw-border-l tw-border-l-gray-200 tw-border-y-gray-300 tw-border-r-gray-300 tw-rounded-r-md tw-cursor-pointer tw-text-lg tw-transition-colors tw-duration-150 hover:tw-border-gray-400 focus:tw-border-gray-600 tw-bg-white tw-h-full"
                >
                    <i class="mdi mdi-menu-down"></i>
                </div>
            </template>
            <template v-if="currentSupplier">
                <ul class="context-menu">
                    <li>
                        <a v-if="editable" href="#" @click.prevent="resetSelection()">
                            <i class="mdi mdi-swap-vertical-bold mr-1"></i>
                            Change
                        </a>
                    </li>
                    <template v-if="Auth.can('manage suppliers')">
                        <li v-if="!currentSupplier.is_system">
                            <a href="#" @click.prevent="navigateToSupplier">
                                <i class="mdi mdi-pencil mr-1"></i>
                                Edit {{ currentSupplier.name }}
                            </a>
                        </li>
                        <template
                            v-if="
                                !currentSupplier.has_supplier_account &&
                                !currentSupplier.is_archived &&
                                !currentSupplier.is_system &&
                                Auth.can('manage suppliers')
                            "
                        >
                            <li class="separator"></li>
                            <li>
                                <a href="#" @click.prevent="inviteSupplier">
                                    <i class="mdi mdi-email-fast mr-1"></i>
                                    Invite to Eventwise
                                </a>
                            </li>
                        </template>
                    </template>
                </ul>
            </template>
        </Menu>
        <ModalCreateSupplier
            ref="createSupplierModal"
            :object-label="objectLabel"
            @created="supplierCreated"
        ></ModalCreateSupplier>
        <ModalSupplierInvite v-if="Auth.can('manage users')" />
    </div>
</template>
<script setup lang="ts">
import { useDataStore } from '@/js/stores/DataStore.js';
import ModalCreateSupplier from '@/js/components/modals/Suppliers/CreateSupplier.vue';
import ModalSupplierInvite from '@/js/components/pages/Portal/InviteSupplier.vue';
import Selector from '@/js/components/controls/Selector.vue';
import Menu from '@/js/components/controls/Menu.vue';
import Tooltip from '@/js/components/Tooltip.vue';
import { toast, Auth } from '@/js/utils.js';
import { onMounted, ref, computed, useTemplateRef } from 'vue';
import type { Supplier } from '@/js/types/supplier.ts';
import { Eventbus } from '@/js/utils/eventbus.ts';

const props = defineProps({
    type: {
        type: [String, Number],
        default: 0,
    },
    objectLabel: {
        type: String,
        default: 'supplier',
    },
    editable: {
        type: Boolean,
        default: true,
    },
    autoDefault: {
        type: Boolean,
        default: false,
    },
});
const model = defineModel<number | null>();
const store = useDataStore();
const availableSuppliers = ref<Supplier[]>([]);
const isLoading = ref(true);
const selector = useTemplateRef('selector');
const currentSupplier = computed(() => {
    return store.get('account.suppliers.' + model.value);
});
const createSupplierModal = useTemplateRef('createSupplierModal');
const filteredSuppliers = computed(() => {
    return availableSuppliers.value.filter((s) => !s.is_archived);
});
const dropdownVisible = computed(() => {
    return (
        currentSupplier.value &&
        (props.editable ||
            (!currentSupplier.value.is_system && Auth.can('manage suppliers')) ||
            (!currentSupplier.value.has_supplier_account &&
                !currentSupplier.value.is_archived &&
                !currentSupplier.value.is_system &&
                Auth.can('manage suppliers')))
    );
});
onMounted(() => {
    reloadSuppliers();
});
const reloadSuppliers = () => {
    availableSuppliers.value = (Object.values(store.get('account.suppliers')) as Supplier[]).filter(
        (supplier) => supplier.supplier_type === props.type
    );
    isLoading.value = false;
    if (!model.value && props.autoDefault) {
        const defaultSupplier = availableSuppliers.value.find((s) => s.is_system);
        model.value = defaultSupplier ? defaultSupplier.id : null;
    }
};
const createSupplier = (supplierName: string) => {
    createSupplierModal.value?.open({
        supplierName: supplierName,
    });
};
const supplierCreated = (supplier: Supplier) => {
    availableSuppliers.value.push(supplier);
    model.value = supplier.id;
    store.set('account.suppliers.' + model.value, supplier);
};
const navigateToSupplier = () => {
    if (!model.value) return;
    Eventbus.$emit('view:supplier', model.value);
};
const resetSelection = () => {
    model.value = null;
    selector.value?.setFocus();
};
const inviteSupplier = () => {
    if (!currentSupplier.value) {
        return;
    } else if (currentSupplier.value.has_supplier_account) {
        toast('Error', 'This supplier already has an active contact account.', 'error');
        return;
    }
    Eventbus.$emit('inviteSupplierModal:open', currentSupplier.value.id);
};

defineExpose({
    reloadSuppliers,
    selector,
});
</script>
<style lang="scss" scoped>
.selector-actions-dropdown {
    padding: 0 4px;
    border: 1px solid rgba(black, 0.15);
    border-left: none;
    font-size: 19.5px;
    border-top-right-radius: 3px;
    border-bottom-right-radius: 3px;
    cursor: pointer;

    &:hover {
        border-color: rgba(black, 0.2);
    }
}
</style>
